import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Grid, Menu } from "antd";
import { connect, useDispatch } from "react-redux";
import { SIDE_NAV_LIGHT } from "constants/ThemeConstant";
import utils from "utils";
// import { onMobileNavToggle } from "redux/actions/Theme";
import { usePermissions } from "../../data/hooks/usePermissions";
import { onMobileNavToggle } from "../../redux/actions/Theme";
import styled from "styled-components";
import Icon, { ExclamationCircleFilled, LogoutOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { useAuth } from "../../data/hooks/useAuth";
import { useTranslation } from "react-i18next";
import navigationConfig from "configs/NavigationConfig";
import { useTheme } from "../../data/hooks/theme/useTheme";
import { getOpenNavKeys, getSelectedNavKeys } from "../../functions/navigation";

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const SideNavContent = (props) => {
  const { hideGroupTitle } = props;
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { uid: userId, email: authEmail, signOut } = useAuth();
  const { can } = usePermissions();
  const { sideNavTheme, navCollapsed, toggleMobileNav } = useTheme();
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
  const closeMobileNav = () => {
    if (isMobile) {
      dispatch(onMobileNavToggle(false));
    }
  };
  // Een item zonder `permission` is voor iedereen; anders beslist het
  // permissiemodel (functions/permissions.js). Rolnamen komen hier niet voor.
  const hasAccess = (menu) => !menu.permission || can(menu.permission);

  // Welk menu-item bij deze URL hoort staat in functions/navigation.js. Hier
  // stond `defaultSelectedKeys`, en dat is precies de bug (BMX-22): Ant Design
  // gebruikt dat alleen bij het eerste renderen en beheert de selectie daarna
  // zelf. Klikken in het menu werkte daardoor wel, maar doorklikken vanuit een
  // pagina — van een inkooporder naar een verzending of factuur — liet de
  // zijbalk op het oude item staan.
  const selectedKeys = useMemo(() => getSelectedNavKeys(pathname), [pathname]);
  const routeOpenKeys = useMemo(() => getOpenNavKeys(pathname), [pathname]);

  // openKeys wordt bijgestuurd, niet afgedwongen: bij het navigeren klapt de
  // sectie van de huidige pagina open, maar wat de gebruiker zelf open- of
  // dichtklapte blijft verder staan.
  const [openKeys, setOpenKeys] = useState(routeOpenKeys);
  useEffect(() => {
    if (routeOpenKeys.length === 0) return;
    setOpenKeys((previous) => {
      const missing = routeOpenKeys.filter((key) => !previous.includes(key));
      // Dezelfde referentie teruggeven als er niets bijkomt, anders rendert het
      // menu bij elke navigatie opnieuw zonder dat er iets verandert.
      return missing.length > 0 ? [...previous, ...missing] : previous;
    });
  }, [routeOpenKeys]);


  return (
    <div
      style={{
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          overflowX: "hidden",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        <Menu
          theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
          mode="inline"
          style={{ borderRight: 0 }}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          className={hideGroupTitle ? "hide-group-title" : ""}
          items={navigationConfig.filter(hasAccess).map((menu) => {
            if (menu.submenu.length > 0) {
              // Group with submenu items
              const children = menu.submenu
                .filter((subMenuFirst) => hasAccess(subMenuFirst))
                .filter((subMenuFirst) => !subMenuFirst.hidden)
                .map((subMenuFirst) => {
                  if (subMenuFirst.submenu.length > 0) {
                    // Nested submenu
                    const nested = subMenuFirst.submenu
                      .filter((subMenuSecond) => hasAccess(subMenuSecond))
                      .filter((subMenuSecond) => !subMenuSecond.hidden);
                    if (nested.length === 0) return null;
                    return {
                      key: subMenuFirst.key,
                      icon: subMenuFirst.icon ? <Icon type={subMenuFirst?.icon} /> : null,
                      label: t(subMenuFirst.title),
                      children: nested.map((subMenuSecond) => ({
                          key: subMenuSecond.key,
                          icon: subMenuSecond.icon ? <Icon type={subMenuSecond?.icon} /> : null,
                          label: (
                            <Link onClick={() => closeMobileNav()} to={subMenuSecond.path}>
                              {t(subMenuSecond.title)}
                            </Link>
                          ),
                        })),
                    };
                  } else {
                    // Single menu item
                    return {
                      key: subMenuFirst.key,
                      icon: subMenuFirst.icon ? <Icon type={subMenuFirst.icon} /> : null,
                      label: (
                        <Link onClick={() => closeMobileNav()} to={subMenuFirst.path}>
                          {t(subMenuFirst.title)}
                        </Link>
                      ),
                    };
                  }
                })
                .filter(Boolean);

              // Groep zonder zichtbare items (bijv. "Inkoop" voor een verkoper) weglaten.
              if (children.length === 0) return null;

              return {
                key: menu.key,
                label: t(menu.title),
                type: "group",
                children: children,
              };
            } else {
              // Single menu item without submenu
              return {
                key: menu.key,
                icon: menu.icon ? <Icon type={menu?.icon} /> : null,
                label: menu.path ? (
                  <Link onClick={() => closeMobileNav()} to={menu.path}>
                    {t(menu?.title)}
                  </Link>
                ) : (
                  t(menu?.title)
                ),
              };
            }
          }).filter(Boolean)}
        />
      </div>
      {/* Bottom section with user controls - only show when not collapsed */}
      {!navCollapsed && (
        <LowerMenu
          style={{
            borderTop: sideNavTheme === SIDE_NAV_LIGHT ? "1px solid #f0f0f0" : "1px solid #303030",
            backgroundColor: sideNavTheme === SIDE_NAV_LIGHT ? "#f9f9f9" : "#001529",
            marginBottom: 0,
          }}
        >
          {/* Language selector */}
          {/*<div style={{ marginBottom: "12px" }}>*/}
          {/*  <Radio.Group*/}
          {/*    options={[*/}
          {/*      { label: "English", value: "en" },*/}
          {/*      { label: "Nederlands", value: "nl" },*/}
          {/*    ]}*/}
          {/*    onChange={(e) => {*/}
          {/*      console.log(e);*/}
          {/*      changeLanguage(e.target.value);*/}
          {/*    }}*/}
          {/*    value={i18n.language}*/}
          {/*    optionType="button"*/}
          {/*    buttonStyle="solid"*/}
          {/*    size="small"*/}
          {/*  />*/}
          {/*</div>*/}

          <LowerMenuItem style={{ cursor: "default" }}>
            <UserOutlined /> <span style={{ marginLeft: "3px" }}>{authEmail}</span>
          </LowerMenuItem>

          {/*<LowerMenuItem>*/}
          {/*  <Link to={urls.profile} style={{ color: "inherit", textDecoration: "none" }}>*/}
          {/*    <SettingOutlined /> <span style={{ marginLeft: "3px" }}>{t("profile_settings")}</span>*/}
          {/*  </Link>*/}
          {/*</LowerMenuItem>*/}

          <LowerMenuItem
            onClick={(e) => {
              e.preventDefault();
              signOut();
            }}
          >
            <LogoutOutlined /> <span style={{ marginLeft: "3px" }}>{t("logout")}</span>
          </LowerMenuItem>
        </LowerMenu>
      )}
    </div>
  );
};

const LowerMenu = styled.ul`
  flex-shrink: 0;
  padding: 16px;
  list-style: none;
`;

const LowerMenuItem = styled.li`
  margin-bottom: 12px;
  transition: color 0.2s ease;

  &:not(:first-child):hover {
    cursor: pointer;
    color: rgb(62, 121, 247);
  }
`;

const MenuContent = (props) => {
  return <SideNavContent {...props} />;
};

const mapStateToProps = ({ theme }) => {
  const { sideNavTheme, topNavColor } = theme;
  return { sideNavTheme, topNavColor };
};

export default connect(mapStateToProps, null)(MenuContent);
